import React, { useState, useEffect } from 'react';
import { Text, View, Pressable, Linking, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import IconButton from '@/components/General/IconButton';
import { describeFormResources, getFormResourceUrl } from '@/data/formResources';
import { normalizeResourcePaths, resourceStableId } from '@/utils/formResourcePolicy';

const basename = (path) => String(path || '').split('/').pop() || 'document';

/**
 * Renders a form's attached documents for respondents + reviewers (plan §7.5).
 * Paths come from the form row / snapshot; descriptors resolve on demand and
 * every tap mints a fresh short-lived signed URL — URLs are never stored.
 *
 * On public forms `publicId` is passed instead of formID so anonymous
 * respondents get URLs scoped to the public link.
 *
 * `resources` is the descriptor array returned by get-public-form; when
 * provided it is authoritative and NO describe call is made — describe is
 * only a fallback for contexts that lack descriptors (entry snapshots,
 * editor staging).
 */
const FormResources = ({ formID = null, publicId = null, paths, resources = null, title = 'Documents' }) => {
    // `paths` is the persisted order; when only descriptors are available
    // (public form load, entry content snapshots) the descriptor order is the
    // accepted order — derive paths from it.
    const normalized = normalizeResourcePaths(
        Array.isArray(paths) && paths.length ? paths : (resources || []).map((d) => d?.path),
    );
    const [extraDescriptors, setExtraDescriptors] = useState({});
    const [openError, setOpenError] = useState(null);
    const descriptors = resources == null ? extraDescriptors
        : Object.fromEntries((resources || []).filter((d) => d?.path).map((d) => [d.path, d]));

    useEffect(() => {
        if (resources != null) return undefined; // load response supplied descriptors
        let cancelled = false;
        const missing = normalized.filter((p) => !extraDescriptors[p]);
        if (!missing.length || (!formID && !publicId)) return undefined;
        describeFormResources({ formID, publicId, paths: missing })
            .then((list) => {
                if (cancelled) return;
                setExtraDescriptors((prev) => {
                    const next = { ...prev };
                    for (const d of list || []) {
                        if (d?.path) next[d.path] = d;
                    }
                    return next;
                });
            })
            .catch(() => { /* rows render basename fallback */ });
        return () => { cancelled = true; };
    }, [normalized.join('|'), formID, publicId, resources == null]);

    if (!normalized.length) return null;

    const open = async (path, disposition = 'inline') => {
        setOpenError(null);
        // Popup blockers demand window.open inside the user gesture — open a
        // blank tab synchronously, then navigate it once signing resolves.
        // `noopener` already severs window.opener, so a blank tab is safe.
        let win = null;
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            win = window.open('', '_blank', 'noopener');
        }
        try {
            const { url } = await getFormResourceUrl({ formID, publicId, path, disposition });
            if (Platform.OS === 'web') {
                if (win) {
                    win.location.href = url;
                } else {
                    // Popup was blocked — last-ditch open attempt, then explain.
                    const retry = window.open(url, '_blank', 'noopener');
                    if (!retry) setOpenError('Your browser blocked the document — allow popups and try again.');
                }
            } else {
                await Linking.openURL(url);
            }
        } catch (err) {
            win?.close?.();
            setOpenError(err?.message || 'Couldn\u2019t open that document. Try again.');
        }
    };

    return (
        <View style={styles.container} testID="form-resources">
            <Text style={styles.title}>{title}</Text>
            {normalized.map((path) => {
                const descriptor = descriptors[path];
                const missing = descriptor?.missing === true;
                return (
                    <Pressable
                        key={path}
                        style={styles.row}
                        disabled={missing}
                        onPress={() => open(path)}
                        testID={`form-resource-${resourceStableId(path)}`}
                    >
                        <Unicon name="document-text-outline" size={18} colorVariant={missing ? 'textSecondary' : 'forms'} />
                        <Text style={[styles.rowName, missing && styles.rowMissing]} numberOfLines={1}>
                            {descriptor?.filename || basename(path)}{missing ? ' (unavailable)' : ''}
                        </Text>
                        {!missing && (
                            <IconButton
                                iconName="download-outline"
                                iconSize={16}
                                type="textSecondary"
                                onPress={() => open(path, 'attachment')}
                                tooltipText="Download"
                                testID={`form-resource-download-${resourceStableId(path)}`}
                            />
                        )}
                        {!missing && <Unicon name="open-outline" size={16} colorVariant="textSecondary" />}
                    </Pressable>
                );
            })}
            {!!openError && <Text style={styles.error}>{openError}</Text>}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }) => ({
    container: {
        backgroundColor: colors.formsContainer,
        borderRadius: roundness.lg,
        padding: spacing.md,
        gap: spacing.xs,
    },
    title: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: typography.caption,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 2,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        paddingVertical: spacing.xs,
    },
    rowName: {
        flex: 1,
        color: colors.forms,
        fontFamily: fonts.heading,
        fontSize: typography.body,
        textDecorationLine: 'underline',
    },
    rowMissing: {
        color: colors.textSecondary,
        textDecorationLine: 'none',
    },
    error: {
        color: colors.error,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        paddingTop: 2,
    },
}));

export default FormResources;
